import { useAuth } from '../contexts/AuthContext';
import { useAccountClaims } from './useAccountClaims';
import { MANAGER_ROLES } from '../types';
import { ADMIN_ROLES as ADMIN_ROLES_SET } from '../rbac/roles';

/**
 * Array projection of the Wave 3 canonical ADMIN_ROLES set, suitable for
 * passing to `useHasAccess(allowedRoles: readonly string[])`.
 */
const ADMIN_ROLES: readonly string[] = Array.from(ADMIN_ROLES_SET);

/**
 * Centralised tenant-role + account_owner-bypass check used by every
 * admin/manager-gated page. Mirrors RoleGuard's pass conditions:
 *
 *   1. user.role           ∈ allowedRoles
 *   2. any user.roles[]    ∈ allowedRoles
 *   3. account_role === 'account_owner'   (bypass — owner of the account
 *      is implicitly an admin of every tenant under it)
 *
 * Always prefer this over local hardcoded role arrays + .includes()
 * checks so future role additions (e.g. tenant_admin) don't have to be
 * grep-and-replaced across the codebase.
 */
export function useHasAccess(allowedRoles: readonly string[]): boolean {
  const { user } = useAuth();
  const { accountRole } = useAccountClaims();

  if (!user) return false;

  if (accountRole === 'account_owner') return true;

  const roles: string[] = [
    ...(user.role ? [user.role] : []),
    ...((user.roles as string[] | undefined) ?? []),
  ];
  return roles.some((r) => allowedRoles.includes(r));
}

/** Convenience: true if user can view tenant-admin features. */
export function useHasAdminAccess(): boolean {
  return useHasAccess(ADMIN_ROLES);
}

/** Convenience: true if user can view manager-tier features (admin + sales managers). */
export function useHasManagerAccess(): boolean {
  return useHasAccess(MANAGER_ROLES);
}
